// The live-preview iframe: owns the frozen postMessage handshake with
// frontend/widget's preview mode (frontend/widget/src/preview.ts). Ported
// from ewt-admin's own preview-frame.tsx — same handshake shape, renamed
// message types. Deliberately takes an already-built `WidgetConfig` rather
// than a survey/template pair — resolving those into a draft (draft.ts) and
// resolving asset URLs lives one layer up, in preview-panel.tsx, which keeps
// this component context-free and synchronously testable with a fake config.
//
// Protocol (frozen by the widget's preview mode, frontend/widget/src/preview.ts):
//   iframe -> parent: {type: "surveys-preview-ready"}   (on load, and again
//                                                          on every reload)
//   parent -> iframe: {type: "surveys-preview", config, startAt?, sentiment?}
//                                                        (targetOrigin "*" —
//                                                          the payload only
//                                                          drives rendering
//                                                          inside an iframe
//                                                          we already
//                                                          control, so an
//                                                          origin check
//                                                          would add config,
//                                                          not security;
//                                                          matches the
//                                                          widget's own side
//                                                          of this contract).
//                                                          `startAt`/
//                                                          `sentiment` are
//                                                          forwarded from
//                                                          `start` unclamped
//                                                          — the widget owns
//                                                          the clamp.
// A (re-)ready message gets an immediate, undebounced reply — a reloaded
// iframe has no config to show until this arrives, so waiting the usual
// 150ms would flash it blank. Every other config change is debounced 150ms
// so a fast typist doesn't flood the iframe with posts.

import { useEffect, useRef, useState } from "react";
import type { WidgetConfig } from "./widget-contract";

const POST_DEBOUNCE_MS = 150;

/* Kiosk: the widget lays itself out at whatever width it is given, so
   rendering it into a narrow panel wraps rows a real kiosk never wraps — a
   five-icon scale breaking onto two lines is the preview's own doing, not
   the design's. Render at a counter tablet in portrait (wide enough that the
   widest scale the product offers still sits on one row) and scale the
   result down instead: the layout is computed at the width a guest actually
   sees, and only the picture is smaller.

   Inline: an embed fills whatever column its host gives it, so the panel's
   own width is the honest one. It renders there at 1:1, as tall as the
   widget reports itself (the constant height it measures across every step,
   the same number a host page's iframe is sized to). */
const KIOSK = { width: 768, height: 1024 } as const;
/** Until the widget has reported its height. */
const INLINE_FALLBACK_HEIGHT = 640;

function isReadyMessage(data: unknown): boolean {
    return typeof data === "object" && data !== null && (data as { type?: unknown }).type === "surveys-preview-ready";
}

function reportedHeight(data: unknown): number | null {
    if (typeof data !== "object" || data === null) return null;
    const message = data as { type?: unknown; height?: unknown };
    if (message.type !== "surveys-height" || typeof message.height !== "number") return null;
    return message.height > 0 ? message.height : null;
}

/** The step (by position) or the closing screen to open the preview on, plus
 *  which set a split step should show. Both fields forward straight to the
 *  widget's own preview mode (frontend/widget/src/preview.ts) unclamped —
 *  `startAt` past the end of the steps is the widget's to clamp (see
 *  global-constraints.md's review focus #4), not this component's. */
export interface PreviewStart {
    startAt?: number | "thanks";
    sentiment?: "negative" | "positive";
}

export interface PreviewFrameProps {
    config: WidgetConfig;
    start?: PreviewStart;
    /** Bumped by the caller to force a repost even when `config` and `start`
     *  are unchanged (Play from start restarts the same flow from step 0). */
    restartKey?: number;
}

function previewMessage(config: WidgetConfig, start: PreviewStart | undefined) {
    return {
        type: "surveys-preview",
        config,
        ...(start?.startAt !== undefined ? { startAt: start.startAt } : {}),
        ...(start?.sentiment !== undefined ? { sentiment: start.sentiment } : {}),
    };
}

export function PreviewFrame({ config, start, restartKey }: PreviewFrameProps) {
    const iframeRef = useRef<HTMLIFrameElement>(null);

    // Latest-value ref: the ready handler (registered once, deps: []) needs
    // the current draft even though it isn't itself a dependency of that
    // effect — see the debounce effect below for why `config` can't just be
    // added to the ready-listener's deps instead.
    const configRef = useRef(config);
    configRef.current = config;
    const startRef = useRef(start);
    startRef.current = start;
    const readyRef = useRef(false);
    const [ready, setReady] = useState(false);
    const boxRef = useRef<HTMLDivElement>(null);
    // 1 until measured, and in any environment without ResizeObserver
    // (happy-dom under test), which simply renders it unscaled.
    const [scale, setScale] = useState(1);
    const [inlineHeight, setInlineHeight] = useState<number | null>(null);
    const inline = config.placement.mode === "inline";

    // Kiosk only. Scale to whatever width the panel has, never up: a panel
    // wider than the tablet shows it at its true size rather than a
    // blown-up one.
    useEffect(() => {
        const box = boxRef.current;
        if (inline || !box || typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect.width ?? 0;
            if (width <= 0) return;
            // This box's own height is derived from the scale, so a naive
            // set here feeds the observer its own output forever. Only the
            // width matters, and an unchanged scale must return the same
            // value so React bails out of the render.
            setScale((previous) => {
                const next = Math.min(1, width / KIOSK.width);
                return Math.abs(next - previous) < 0.001 ? previous : next;
            });
        });
        observer.observe(box);
        return () => observer.disconnect();
    }, [inline]);

    // Registered once: re-subscribing on every draft change would risk
    // missing a message during the gap between removing the old listener and
    // attaching the new one.
    useEffect(() => {
        function onMessage(event: MessageEvent) {
            if (event.source !== iframeRef.current?.contentWindow) return;
            const height = reportedHeight(event.data);
            if (height !== null) {
                setInlineHeight(height);
                return;
            }
            if (!isReadyMessage(event.data)) return;
            readyRef.current = true;
            setReady(true);
            iframeRef.current?.contentWindow?.postMessage(previewMessage(configRef.current, startRef.current), "*");
        }
        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
    }, []);

    // Debounced post on every draft change. Deliberately keyed on `config`
    // alone (not `ready`) — `readyRef` is read instead of `ready` state so
    // this effect does not also fire the moment the ready listener above
    // flips `ready` to true, which would race the immediate post it just
    // sent with a redundant debounced one 150ms later.
    //
    // `start`'s fields are listed individually (not `start` itself) so a new
    // object with the same values — the panel rebuilds `start` on every
    // render — doesn't repost. `restartKey` is listed so Play from start
    // (same config, same empty `start`) still forces a repost.
    // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above — start's fields, not start, are the real deps; restartKey is a deliberate extra one
    useEffect(() => {
        if (!readyRef.current) return;
        const timer = setTimeout(() => {
            iframeRef.current?.contentWindow?.postMessage(previewMessage(config, start), "*");
        }, POST_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [config, start?.startAt, start?.sentiment, restartKey]);

    const height = inlineHeight ?? INLINE_FALLBACK_HEIGHT;
    return (
        <div className="flex flex-col gap-2">
            <div
                ref={boxRef}
                className="w-full overflow-hidden rounded-kore-md border border-kore-border bg-kore-surface-sunken"
                // Inline: no height of its own, so the border wraps the iframe
                // rather than clipping its last pixels.
                style={inline ? undefined : { height: KIOSK.height * scale }}
            >
                <iframe
                    ref={iframeRef}
                    title="Live preview"
                    src="/s/preview"
                    style={
                        inline
                            ? { border: "none", display: "block", width: "100%", height }
                            : {
                                  border: "none",
                                  width: KIOSK.width,
                                  height: KIOSK.height,
                                  transform: `scale(${scale})`,
                                  transformOrigin: "top left",
                              }
                    }
                />
            </div>
            {!ready && <p className="text-xs text-muted-foreground">Connecting to the preview…</p>}
        </div>
    );
}
